import { useEffect, useState } from 'react'
import useLocale from '@/utils/useLocale'
import { Spin, Input, Message, Upload, Progress } from '@arco-design/web-react'
import { IconClose, IconPlus, IconEdit } from '@arco-design/web-react/icon'
import { CURRENCY_COOKIE, LANG_COOKIE, TOKEN_COOKIE, REGION_COOKIE } from '@/utils/constants'
import { getCookie } from 'cookies-next'
import { baseOrigin } from '@/api/request'

const TextArea = Input.TextArea

const uploadUrl = `${baseOrigin}/api/v1/tools/image/upload`

const UploadImage = (props: {
  onImageSearch?: (file: string, keyword: string) => void
  onClose?: () => void
  imageSearchLoading: boolean
}) => {
  const { onImageSearch, onClose, imageSearchLoading } = props
  const [file, setFile] = useState<any>(null)
  const [textValue, setTextValue] = useState('')
  const [resData, setResData] = useState<string>('')
  const [prevLoading, setPrevLoading] = useState(false)
  const [shouldScroll, setShouldScroll] = useState(false)
  const t = useLocale()
  const token = getCookie(TOKEN_COOKIE)
  const lang = getCookie(LANG_COOKIE)
  const currency = getCookie(CURRENCY_COOKIE)
  const region = getCookie(REGION_COOKIE)

  useEffect(() => {
    // 当imageSearchLoading从true变为false时，滚动到表格
    if (shouldScroll && prevLoading && !imageSearchLoading) {
      const tableElement = document.getElementById('table-sort-wrap')
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: 'smooth' })
        setShouldScroll(false) // 滚动后重置状态
      }
    }
    setPrevLoading(imageSearchLoading)
  }, [imageSearchLoading, shouldScroll, prevLoading])

  return (
    <div
      className="p-3 relative bg-white rounded-lg shadow-lg max-w-5xl mx-auto"
      style={{ height: '226px', width: '1200px' }}
    >
      <IconClose onClick={onClose} className="text-black text-xl absolute top-4 right-4 cursor-pointer" />
      <div className="text-center text-base font-bold mb-4">{t['search_by_image_placeholder']}</div>
      <div className="flex items-stretch justify-center gap-6 h-[180px] px-4">
        <div className="text-center w-[45%] border border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50 hover:border-blue-400 transition-colors flex items-center justify-center h-full">
          <div className="w-full h-full flex flex-col items-center justify-center">
            <Upload
              action={uploadUrl}
              headers={{
                Authorization: `Bearer ${token}`,
                'x-lang': lang as string,
                'x-currency': currency as string,
                'x-region': region as string
              }}
              fileList={file ? [file] : []}
              showUploadList={false}
              onChange={(_, currentFile) => {
                setFile({
                  ...currentFile,
                  url: currentFile?.originFile ? URL.createObjectURL(currentFile.originFile) : ''
                })
                if (currentFile.status === 'done') {
                  setResData((currentFile?.response as any)?.data)
                  Message.success(t['upload_image_success'])
                }
              }}
              onProgress={(currentFile) => {
                setFile(currentFile)
              }}
            >
              <div className={''}>
                {file && file.url ? (
                  <div className="custom-upload-avatar w-full h-full flex items-center justify-center">
                    <div className="relative w-[85%] h-[85%] border rounded-md overflow-hidden">
                      <img src={file.url} className="w-full h-full object-contain" style={{ maxHeight: '160px' }} />
                      <div className="absolute inset-0 hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                        <IconEdit className="text-2xl text-white p-1 bg-black/40 rounded-full cursor-pointer" />
                      </div>
                      {file.status === 'uploading' && file.percent < 100 && (
                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                          <Progress
                            percent={file.percent}
                            type="circle"
                            size="default"
                            className="scale-125"
                            style={{
                              position: 'absolute',
                              left: '50%',
                              top: '50%',
                              transform: 'translateX(-50%) translateY(-50%)'
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center justify-center">
                    <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-r from-[#655aec] to-purple-500 text-white rounded-full mb-2">
                      <IconPlus />
                    </div>
                    <div className="text-sm font-semibold mb-1">{t['upload_image']}</div>
                    <p className="text-xs text-gray-500 text-center">
                      {t['click_or_drag_image_here']}
                      <br />
                      {t['support_image_format']}
                    </p>
                  </label>
                )}
              </div>
            </Upload>
          </div>
        </div>
        <div className="w-[55%] flex flex-col gap-2">
          <TextArea
            value={textValue}
            onChange={(val) => {
              setTextValue(val)
            }}
            placeholder={t['search_by_image_tips']}
            className="h-[180px] w-full border border-gray-300 rounded-lg p-2 flex-grow"
          />
          <div className="flex justify-end">
            <button
              disabled={imageSearchLoading}
              className={`border px-8 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center justify-center min-w-[100px] ${
                imageSearchLoading
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed border-gray-300 opacity-70 hover:shadow-none'
                  : 'bg-gradient-to-r from-[#655aec] to-[#a78bfa] text-white border-transparent cursor-pointer hover:shadow-md hover:shadow-blue-300/50 hover:brightness-105'
              }`}
              onClick={() => {
                if (!file) {
                  Message.error(t['search_by_image_error'])
                  return
                }
                setShouldScroll(true) // 点击搜索按钮时设置滚动标志
                onImageSearch?.(resData, textValue)
              }}
            >
              {imageSearchLoading ? <Spin dot /> : t['search_by_image_button']}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UploadImage
